import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { NAV_SECTIONS, NAV_ACTIONS, canSee } from '@/lib/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { DialogTitle } from '@/components/ui/dialog';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Flatten NAV_SECTIONS into { path, label, icon, keywords, gate } leaf screens (section.items
// + section.groups[].items). Local to the palette — Plan 01's flattenItems() is not exported,
// so this mirrors it for rendering purposes only (canSee stays the single gate resolver).
function flattenScreens() {
  const flat = [];
  for (const section of NAV_SECTIONS) {
    if (section.items) flat.push(...section.items);
    if (section.groups) {
      for (const group of section.groups) {
        if (group.items) {
          for (const item of group.items) {
            flat.push(item.gate ? item : { ...item, gate: group.gate });
          }
        }
      }
    }
  }
  return flat;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const { isAdmin, hasPermission } = useAuth();
  const authCtx = { isAdmin, hasPermission };

  const [open, setOpen] = useState(false);
  const [equipments, setEquipments] = useState([]);
  const [equipmentsLoaded, setEquipmentsLoaded] = useState(false);
  const [equipmentsError, setEquipmentsError] = useState(false);

  // Dual open paths (D-09, Pitfall 7): ⌘K/Ctrl+K keydown AND the sidebar search button's
  // custom event. Single useEffect with cleanup — never duplicate listeners across renders.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpenEvent = () => setOpen(true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('octopus:open-command-palette', onOpenEvent);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('octopus:open-command-palette', onOpenEvent);
    };
  }, []);

  // Own lazy fetch (Pitfall 2 — never the AuthContext equipment list, which is only populated
  // for a subset of roles). Server-side RBAC already filters via rbac.get_visible_equipment_ids.
  const loadEquipments = useCallback(async () => {
    if (equipmentsLoaded) return;
    try {
      const res = await axios.get(`${API}/equipments`);
      setEquipments(Array.isArray(res.data) ? res.data : res.data?.equipments || []);
      setEquipmentsError(false);
    } catch (err) {
      setEquipmentsError(true);
    } finally {
      setEquipmentsLoaded(true);
    }
  }, [equipmentsLoaded]);

  useEffect(() => {
    if (open) loadEquipments();
  }, [open, loadEquipments]);

  const goTo = (path) => {
    setOpen(false);
    navigate(path);
  };

  const screens = flattenScreens().filter((item) => canSee(item, authCtx));
  const actions = NAV_ACTIONS.filter((item) => canSee(item, authCtx));

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <DialogTitle className="sr-only">Busca global</DialogTitle>
      <CommandInput placeholder="Buscar telas, equipamentos e ações…" />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>

        <CommandGroup heading="Telas">
          {screens.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.path}
                value={item.label}
                keywords={[...(item.keywords || []), item.label]}
                onSelect={() => goTo(item.path)}
              >
                {Icon ? <Icon /> : null}
                <span>{item.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Equipamentos">
          {equipmentsError ? (
            <CommandItem disabled value="equipamentos-erro">
              Não foi possível carregar os equipamentos.
            </CommandItem>
          ) : (
            equipments.map((eq) => (
              <React.Fragment key={eq.id}>
                <CommandItem
                  value={`monitorar-${eq.tag}`}
                  keywords={[eq.tag, eq.name]}
                  onSelect={() => goTo(`/equipments/${eq.id}/monitor`)}
                >
                  <span>{`Monitorar ${eq.tag}`}</span>
                </CommandItem>
                <CommandItem
                  value={`ficha-${eq.tag}`}
                  keywords={[eq.tag, eq.name]}
                  onSelect={() => goTo(`/equipments/${eq.id}/monitor?tab=historico`)}
                >
                  <span>{`Ficha do ${eq.tag}`}</span>
                </CommandItem>
                {eq.uses_fuel ? (
                  <CommandItem
                    value={`abastecimento-${eq.tag}`}
                    keywords={[eq.tag, eq.name, 'abastecer', 'combustível']}
                    onSelect={() =>
                      goTo(`/operations/fuel-control?equipment=${encodeURIComponent(eq.id)}&action=refuel`)
                    }
                  >
                    <span>{`Lançar abastecimento no ${eq.tag}`}</span>
                  </CommandItem>
                ) : null}
              </React.Fragment>
            ))
          )}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Ações">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <CommandItem
                key={action.id}
                value={action.label}
                keywords={[...(action.keywords || []), action.label]}
                onSelect={() => goTo(action.path)}
              >
                {Icon ? <Icon /> : null}
                <span>{action.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
